import asyncBatch from 'async-batch';
import { AxiosRequestConfig } from 'axios';
import { FormikHelpers } from 'formik';
import { Duration } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import Axios from '../../../../../common/axiosInterceptor';
import { Logger } from '../../../../../common/Logger';
import {
  loadableEnum, 
  useMakePHPRequest,
} from '../../../../../common/useMakePHPRequest';
import { isUrl } from '../../../../../common/validators';
import { Business } from '../../../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { IUserAtom, user } from '../../../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../../../services/rest/makeAPIRequest';
import { getDefaultActionUrl } from '../../../../components/dropdowns/DefaultAction';
import { useSnackbarWithStyles } from '../../../../components/notifications/SnackbarProviderWithStyles';
import {
  IQueueEditFormInitialValues,
  QueueEditForm,
  sleep,
} from './QueueEditForm';
import { IQueueTeamsContent } from './QueueTeams';

async function uploadFile(payload: { [key: string]: any }) {
  const data = new FormData();
  // append key/value to form data
  Object.entries(payload).forEach(([key, value]) => {
    data.append(key, value);
  });

  const requestConfig: AxiosRequestConfig = {
    method: 'post',
    url: `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
    headers: {
      'content-type': 'multipart/form-data',
    },
    withCredentials: true,
    data,
  };

  // make ajax request
  let response, exception;
  try {
    response = await Axios(requestConfig);
  } catch (ex) {
    exception = ex;
  }

  // log post
  Logger.debug({
    methodName: payload.call,
    requestConfig,
    response,
    exception,
  });

  return {
    status: exception ? loadableEnum.HAS_ERROR : loadableEnum.HAS_VALUE,
  };
}

interface IBusinessQueue {
  active: string;
  autoAnswerInbound: string;
  autoAnswerReturnedCall: string;
  businessid: string;
  created: string;
  defaultAction: string;
  defaultuser: string;
  email: string;
  greeting: string;
  vmGreeting: string;
  hasGreeting: 'yes' | 'no';
  hasRingMusic: 'yes' | 'no';
  inQueueCallback: string;
  inQueueIVR: string;
  inQueueVoicemail: string;
  matchState: string;
  maxduration: string;
  playWaitTime: string;
  queueType: string;
  queueid: string;
  queuename: string;
  ringmusic: string;
  ringtimeout: string;
  updated: string;
  vDndAfterMissedCallmGreeting: string;
  DndAfterMissedCall: '1' | '0';
  subscribeOnLogin: '1' | '0';
  assignOwnerOnAnswer: '1' | '0';
  overrideOwnerOnAnswer: '1' | '0';
  initialAttempt:
    | 'ignoreOwnership'
    | 'routeToOwnerOnly'
    | 'routeToOwnerThenProceed';
  useBusinessHoursEnabled: boolean;
  queueHours: any;
  closedAction: string;
  ringStrategy: 'linear' | 'simultaneous';
}

/**
 * Interface for form submission
 * extends record so object can by accessed by string key
 */
interface IBusinessQueueForSubmit extends Record<string, any> {
  queuename: string;
  email: string;
  maxduration: string;
  ringtimeout: string;
  // if greeting not empty then see if mp3 or tts if is a url
  greetingType: 'mp3' | 'tts' | 'none';
  greeting: string;
  voicemailType: 'mp3' | 'tts';
  voicemail: string;
  musicType: 'mp3';
  playWaitTime: boolean;
  autoAnswerInbound: boolean;
  inQueueIVR: boolean;
  inQueueVoicemail: boolean;
  inQueueCallback: boolean;
  autoAnswerReturnedCall: boolean;
  defaultAction: string;
  queueTeams: IQueueTeamsContent[];
  DndAfterMissedCall: boolean;
  subscribeOnLogin: boolean;
  assignOwnerOnAnswer: boolean;
  overrideOwnerOnAnswer: boolean;
  initialAttempt:
    | 'ignoreOwnership'
    | 'routeToOwnerOnly'
    | 'routeToOwnerThenProceed';
  useBusinessHoursEnabled: boolean;
  queueHours: any;
  closedAction: string;
  ringStrategy: 'linear' | 'simultaneous';
}

interface ISubmitQueue {
  submitQueueEditPayload: any;
  updateQueuePayload: any;
  defaultActionPayload: any;
  uploadedFiles: any;
  queueTeams: IQueueTeamsContent[];
  initialValues: IQueueEditFormInitialValues;
  formikHelpers: FormikHelpers<IQueueEditFormInitialValues>;
  useBusinessHoursEnabled: boolean;
  queueHours: any;
  closedActionPayload: any;
}

interface IQueueEditDialog {
  id?: string;
  show: boolean;
  onHide: () => void;
  setUpdatedRowId?: (rowId: string | undefined) => void;
  showMembersTab?: boolean;
}

/**
 * Function to hide modal
 *
 * @callback onHideCallback
 * @param {Object} event Synthetic react event
 */

/**
 * Queue editing component
 *
 * @param {Object} props
 * @param {string} [props.id] Queue id
 * @param {boolean} props.show True if modal shown
 * @param {onHideCallback} props.onHide Function to hide modal
 *
 * @see https://github.com/microsoft/vscode/issues/41296
 */
export function QueueEditDialog({
  id,
  show,
  onHide,
  setUpdatedRowId,
  showMembersTab,
}: IQueueEditDialog) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [submitState, setSubmitState] = useState(loadableEnum.EMPTY);

  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);

  const userAtomContents = userAtomLoadable?.contents as IUserAtom;
  const phpRequest = useMakePHPRequest({
    userAtom: userAtomContents,
  });

  const businessQueuesLoadable = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );

  const data = {
    call: methodNameEnum.GET_QUEUE_TEAMS,
    queueid: id,
  };
  const queueTeamsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_QUEUE_TEAMS,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );
  const setRefetchToggleForBusinessQueues = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_BUSINESS_QUEUES)
  );
  const setRefetchToggleQueueTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_QUEUE_TEAMS)
  );
  const setRefetchToggleQueueMembers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_QUEUE_MEMBERS)
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business & {
      // eslint-disable-next-line func-call-spacing
      businessHarassmentPolicies: (BusinessHarassmentPolicy & {
        days: number;
        hours: number;
        minutes: number;
      })[];
    }
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtomContents?.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business: Business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );
  // short-circuit find if id is empty
  const queue = useMemo<IBusinessQueue>(
    () =>
      businessQueuesLoadable.state === 'loading' || !id
        ? {}
        : businessQueuesLoadable.contents?.data?.result?.find(
            (queue: any) => queue.queueid === id
          ) ?? {},
    [id, businessQueuesLoadable]
  );
  // all fields must default to not null/undefined or else react will give uncontrolled prop error
  // wrap in useMemo so form change does not cause double render
  const queueForEdit = useMemo<IBusinessQueueForSubmit>(
    () => ({
      queuename: queue.queuename ?? '',
      email: queue.email ?? '',
      maxduration: queue.maxduration ?? '',
      ringtimeout: queue.ringtimeout ?? '',
      // if greeting not empty then see if mp3 or tts if is a url
      greetingType: queue.greeting
        ? isUrl(queue.greeting)
          ? 'mp3'
          : 'tts'
        : 'none',
      greeting: queue.greeting ?? '',
      voicemailType: isUrl(queue.vmGreeting) ? 'mp3' : 'tts',
      voicemail: queue.vmGreeting ?? '',
      musicType: 'mp3',
      holdMusic: queue.ringmusic ?? '',
      playWaitTime: queue.playWaitTime === '1',
      autoAnswerInbound: queue.autoAnswerInbound === '1',
      inQueueIVR: queue.inQueueIVR === '1',
      inQueueVoicemail: queue.inQueueVoicemail === '1',
      inQueueCallback: queue.inQueueCallback === '1',
      autoAnswerReturnedCall: queue.autoAnswerReturnedCall === '1',
      defaultAction: queue.defaultAction ?? '',
      queueTeams:
        queueTeamsLoadable.state === 'loading'
          ? undefined // set to undefined if loading
          : queueTeamsLoadable.contents?.response?.data?.result ?? [],
      DndAfterMissedCall: queue.DndAfterMissedCall === '1',
      subscribeOnLogin: queue.subscribeOnLogin === '1',
      assignOwnerOnAnswer: queue.assignOwnerOnAnswer === '1',
      overrideOwnerOnAnswer: queue.overrideOwnerOnAnswer === '1',
      initialAttempt: queue.initialAttempt ?? 'ignoreOwnership',
      useBusinessHoursEnabled: queue.useBusinessHoursEnabled,
      queueHours: queue.queueHours
        ? JSON.parse(queue.queueHours)
        : businessHoursMemo,
      closedAction: queue.closedAction ?? '',
      ringStrategy: queue.ringStrategy ?? 'linear',
    }),
    [queue, queueTeamsLoadable]
  );

  // for new queue
  const queueForCreate = useMemo<IBusinessQueueForSubmit>(
    () => ({
      queuename: '',
      email: '',
      maxduration: '10',
      ringtimeout: '30',
      // if greeting not empty then see if mp3 or tts if is a url
      greetingType: 'none',
      greeting: '',
      voicemailType: 'tts',
      voicemail:
        'Record your message at the tone, after you are done, press pound',
      musicType: 'mp3',
      playWaitTime: false,
      autoAnswerInbound: false,
      inQueueIVR: true,
      inQueueVoicemail: true,
      inQueueCallback: true,
      autoAnswerReturnedCall: true,
      defaultAction: '',
      queueTeams: [],
      DndAfterMissedCall: true,
      subscribeOnLogin: true,
      assignOwnerOnAnswer: false,
      overrideOwnerOnAnswer: false,
      initialAttempt: 'ignoreOwnership',
      useBusinessHoursEnabled: true,
      queueHours: businessHoursMemo,
      closedAction: '',
      ringStrategy: 'linear',
    }),
    []
  );
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const handleSubmit = useCallback(
    async ({
      submitQueueEditPayload,
      updateQueuePayload,
      defaultActionPayload,
      uploadedFiles,
      queueTeams,
      initialValues,
      formikHelpers,
      useBusinessHoursEnabled,
      queueHours,
      closedActionPayload,
    }: ISubmitQueue) => {
      // Force autoAnswer options to false when ringStrategy is simultaneous
      if (updateQueuePayload.ringStrategy === 'simultaneous') {
        updateQueuePayload.autoAnswerInbound = false;
        updateQueuePayload.autoAnswerReturnedCall = false;
      }

      let errorMessage = '';
      setSubmitState(loadableEnum.LOADING);
      let queueid = id;
      let resMessage: string | undefined;
      let resonseFromSubmitNew;
      // if id not populated then create new queue
      if (!queueid) {
        // const { response, status } = await phpRequest({
        //   methodName: methodNameEnum.SUBMIT_NEW_QUEUE,
        //   payload: {
        // groupname: submitQueueEditPayload.name,
        // vmemail: submitQueueEditPayload.email,
        //   },
        // });
        const data = {
          call: methodNameEnum.SUBMIT_NEW_QUEUE,
          apikey: userAtomContents?.apikey,
          businessid: userAtomContents?.businessid,
          groupname: submitQueueEditPayload.name,
          vmemail: submitQueueEditPayload.email,
        };
        const response = await mutation.mutateAsync({
          endpoint: endpointEnum.SUBMIT_NEW_QUEUE,
          payload: data,
        });
        resonseFromSubmitNew = response;
        queueid = response?.data?.result?.[0]?.queueid;
        resMessage = response?.data?.result?.code;
        if (status === loadableEnum.HAS_ERROR) {
          errorMessage = 'Failed to create new queue';
        }

        Logger.trace(
          {
            message: 'Success on submitNewQueue',
            response,
          },
          'handleSubmit'
        );
      }
      // edit queue settings/toggles if queue exists
      if (queueid) {
        const response = await phpRequest({
          methodName: methodNameEnum.SUBMIT_QUEUE_EDIT,
          payload: {
            queue: JSON.stringify({
              general: {
                ...submitQueueEditPayload,
                defaultAction: getDefaultActionUrl({
                  ...defaultActionPayload,
                  businessid: userAtomContents?.businessid,
                  parentType: 'queue',
                  voicemail: queueid,
                }),
                useBusinessHoursEnabled: useBusinessHoursEnabled ?? true,
                queueHours: queueHours,
                closedAction: getDefaultActionUrl({
                  ...closedActionPayload,
                  businessid: userAtomContents?.businessid,
                  parentType: 'queue',
                  voicemail: queueid,
                }),
              },
            }),
            queueid,
          },
        });
        const duplicateName = response?.response?.data?.code === 1062; // 1062 - duplicate key violation
        if (response.status === loadableEnum.HAS_ERROR || duplicateName) {
          errorMessage = duplicateName
            ? 'Please choose a Queue Name that is not already in use'
            : 'Failed to update queue';
        }
        // upload mp3 file to submitQueueVoicemailMp3File
        // NOTE: only uploads first file in array
        if (
          !errorMessage &&
          uploadedFiles.voicemail &&
          submitQueueEditPayload.voicemailType === 'mp3' &&
          userAtomContents?.businessid
        ) {
          const { status } = await uploadFile({
            call: methodNameEnum.SUBMIT_QUEUE_VOICEMAIL_MP3_FILE,
            businessid: userAtomContents.businessid,
            apiKey: userAtomContents.apikey,
            mp3: uploadedFiles.voicemail[0],
            queueid,
          });
          if (status === loadableEnum.HAS_ERROR) {
            errorMessage = 'Failed to upload voicemail mp3/recording';
          }
        }
        if (
          !errorMessage &&
          uploadedFiles.voicemail &&
          submitQueueEditPayload.voicemailType === 'record' &&
          userAtomContents?.businessid
        ) {
          const formData = new FormData();
          formData.append('webm', uploadedFiles.voicemail[0]);
          formData.append('businessid', userAtomContents.businessid as any);
          formData.append('apikey', userAtomContents?.apikey);
          formData.append('userid', ''); // user id of agent being changed
          formData.append('id', queueid); // user id of agent being changed
          formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
          formData.append('iid', 0 as any); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
          formData.append('name', submitQueueEditPayload.name); // for vm drop name MUST be name of voicemail drop
          formData.append('type', 'queue-voicemail'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
          const _response3 = await Axios.post(
            `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
            formData
          );
        }
        if (
          !errorMessage &&
          uploadedFiles.holdMusic &&
          userAtomContents?.businessid &&
          submitQueueEditPayload.musicType === 'mp3'
        ) {
          const { status } = await uploadFile({
            call: methodNameEnum.SUBMIT_QUEUE_HOLD_MUSIC_MP3_FILE,
            businessid: userAtomContents.businessid,
            apiKey: userAtomContents.apikey,
            mp3: uploadedFiles.holdMusic[0],
            queueid,
          });
          if (status === loadableEnum.HAS_ERROR) {
            errorMessage = 'Failed to upload hold music mp3';
          }
        }

        // upload mp3 file to submitQueueGreetingMp3File
        // NOTE: only uploads first file in array
        if (
          !errorMessage &&
          uploadedFiles.greeting &&
          userAtomContents?.businessid &&
          submitQueueEditPayload.greetingType === 'mp3'
        ) {
          const { status } = await uploadFile({
            call: methodNameEnum.SUBMIT_QUEUE_GREETING_MP3_FILE,
            businessid: userAtomContents.businessid,
            apiKey: userAtomContents.apikey,
            mp3: uploadedFiles.greeting[0],
            queueid,
          });
          if (status === loadableEnum.HAS_ERROR) {
            errorMessage = 'Failed to upload greeting mp3/recording';
          }
        }

        if (
          !errorMessage &&
          uploadedFiles.greeting &&
          userAtomContents?.businessid &&
          submitQueueEditPayload.greetingType === 'record'
        ) {
          const formData = new FormData();
          formData.append('webm', uploadedFiles.greeting[0]);
          formData.append('businessid', userAtomContents.businessid as any);
          formData.append('apikey', userAtomContents?.apikey);
          formData.append('userid', ''); // user id of agent being changed
          formData.append('id', queueid); // user id of agent being changed
          formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
          formData.append('iid', 0 as any); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
          formData.append('name', submitQueueEditPayload.name); // for vm drop name MUST be name of voicemail drop
          formData.append('type', 'queue-greeting'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
          const _response3 = await Axios.post(
            `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
            formData
          );
        }

        // server request for update queues toggles
        // run as async since synchronous atom updates will only trigger one reload
        await asyncBatch(
          Object.entries(updateQueuePayload).filter(([name, value]) =>
            // update queue toggle only if changed
            id ? queueForEdit[name] !== value : queueForCreate[name] !== value
          ),
          async ([name, value]: [name: string, value: string]) => {
            if (!errorMessage) {
              // const { status: statusForUpdateQueue } = await phpRequest({
              //   methodName: methodNameEnum.UPDATE_QUEUE,
              //   payload: {
              // name,
              // value: Number(value), // map bool to 1 or 0
              // queueid,
              //   },
              // });
              const data = {
                call: methodNameEnum.UPDATE_QUEUE,
                apikey: userAtomContents?.apikey,
                businessid: userAtomContents?.businessid,
                name,
                value:
                  typeof value === 'boolean' ? String(Number(value)) : value, // map bool to 1 or 0
                queueid,
              };
              try {
                await mutation.mutateAsync({
                  endpoint: endpointEnum.UPDATE_QUEUE,
                  payload: data,
                });
              } catch (exception) {
                errorMessage = 'Failed to update queue';
              }
            }
            // only assign if current status is not error
            await sleep(100); // pause between calls to reduce load on database
          },
          1 // one at a time
        );

        // add/remove teams
        if (!errorMessage) {
          // find teams that are not found in inital teams
          const teamsToAdd = queueTeams.filter(
            (updatedTeam) =>
              !initialValues.queueTeams.find(
                (initialTeam) => initialTeam.teamid === updatedTeam.teamid
              )
          );
          // find teams that are not found in updated teams
          const teamsToDelete = initialValues.queueTeams.filter(
            (initialTeam) =>
              !queueTeams.find(
                (updatedTeam) => initialTeam.teamid === updatedTeam.teamid
              )
          );

          if (teamsToAdd.length) {
            // const { status } = await phpRequest({
            //   methodName: methodNameEnum.ADD_TEAM_QUEUE,
            //   payload: {
            // queueid,
            // ids: JSON.stringify(teamsToAdd.map((team) => team.teamid)),
            //   },
            // });
            const data = {
              call: methodNameEnum.ADD_TEAM_QUEUE,
              apikey: userAtomContents?.apikey,
              businessid: userAtomContents?.businessid,
              queueid,
              ids: JSON.stringify(teamsToAdd.map((team) => team.teamid)),
            };
            try {
              await mutation.mutateAsync({
                endpoint: endpointEnum.ADD_TEAM_QUEUE,
                payload: data,
              });
            } catch (exception) {
              errorMessage = 'Failed to add team(s)';
            }
          }
          if (!errorMessage && teamsToDelete.length) {
            for (const team of teamsToDelete) {
              // const { status } = await phpRequest({
              //   methodName: methodNameEnum.DELETE_QUEUE_TEAM,
              //   payload: {
              // queueid,
              // teamid: team.teamid,
              //   },
              // });
              const data = {
                call: methodNameEnum.DELETE_QUEUE_TEAM,
                apikey: userAtomContents?.apikey,
                businessid: userAtomContents?.businessid,
                queueid,
                teamid: team.teamid,
              };
              try {
                await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_QUEUE_TEAM,
                  payload: data,
                });
              } catch (exception) {
                errorMessage = 'Failed to delete team(s)';
              }
            }
          }
        }

        setSubmitState(
          errorMessage ? loadableEnum.HAS_ERROR : loadableEnum.HAS_VALUE
        );

        // stop formik submission
        formikHelpers.resetForm();

        if (!errorMessage) {
          setRefetchToggleForNumbers({});
          // reload queue teams
          setRefetchToggleQueueTeams({});
          // reload business queues
          setRefetchToggleForBusinessQueues({});
          // reload queue teams
          setRefetchToggleQueueMembers({});
          // set updated row id
          setUpdatedRowId?.(queueid);
          // hide modal
          onHide();
        }

        Logger.trace(
          {
            message: errorMessage || 'Queue updated Successfully',
            queueid,
          },
          'handleSubmit'
        );
      } else {
        errorMessage =
          resMessage === 'ER_DUP_ENTRY'
            ? 'Please choose a Queue Name that is not already in use'
            : 'Fail to add new queue';
        setSubmitState(loadableEnum.HAS_ERROR);

        // stop formik submission
        formikHelpers.resetForm();

        Logger.trace(
          {
            message: errorMessage,
            resonseFromSubmitNew,
          },
          'submitQueue'
        );
      }

      enqueueSnackbarWithStyles({
        message: errorMessage || 'Update successful',
        alertProps: {
          severity: errorMessage ? 'error' : 'success',
        },
      });

      return queueid;
    },
    [
      id,
      userAtomLoadable,
      setSubmitState,
      setRefetchToggleForBusinessQueues,
      setUpdatedRowId,
      onHide,
      phpRequest,
      enqueueSnackbarWithStyles,
    ]
  );

  return (
    <QueueEditForm
      id={id}
      businessid={userAtomContents?.businessid}
      show={show}
      queue={id ? queueForEdit : queueForCreate}
      isLoading={
        businessQueuesLoadable.state === 'loading' ||
        submitState === loadableEnum.LOADING
      }
      onSubmit={handleSubmit}
      onHide={onHide}
      onTeamChange={() => {
        setRefetchToggleQueueTeams({});
        setRefetchToggleQueueMembers({});
      }}
      businessLoadable={businessLoadable}
      businessCached={businessCached}
      showMembersTab={showMembersTab}
    />
  );
}
